/**
 * Edit Team Component - Gaming UI
 * Form to edit existing team details with gaming layout
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import { Input } from "../ui/Input";
import teamService from "../../services/api/teamService";
import { getUserSports, getAllSports } from "../../services/api/sportsService";
import VenueSearch from "../ui/VenueSearch";
import "../../styles/features/teams.css";

const EditTeam = ({ currentUser, token, onLogout, onBack }) => {
  const { teamId } = useParams();
  const navigate = useNavigate();

  const [team, setTeam] = useState(null);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    sport_id: "",
    sport: "",
    organization: "",
    location: "",
    logo_url: "",
    is_public: true,
    skill_level: "",
    requires_approval: false,
    max_members: 25,
    rules: {
      substitutions: 1,
      age_restriction: "none",
    },
  });



  useEffect(() => {
    fetchSportsAndTeamData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]); // Only re-fetch if teamId changes

  const fetchSportsAndTeamData = async () => {
    try {
      // Fetch user's sports first, fallback to all sports
      let sportsData = [];
      try {
        const userRes = await getUserSports();
        sportsData = userRes?.data?.sports || userRes?.sports || userRes?.data || [];
      } catch { /* fall through */ }
      if (!sportsData.length) {
        const allRes = await getAllSports();
        sportsData = allRes.data || [];
      }
      setSports(sportsData);
      
      // Then fetch team data
      await fetchTeamData(sportsData);
    } catch (err) {
      console.error("Error fetching sports:", err);
      setError("Failed to load sports data");
      setLoading(false);
    }
  };

  const fetchTeamData = async (sportsData) => {
    try {
      setLoading(true);
      setError("");

      const response = await teamService.getTeam(teamId);
      
      // Extract the actual team data from the response
      const responseData = response.data || response;
      const teamData = responseData.team || responseData;
      setTeam(teamData);

      // Handle settings object if it exists (some team data is stored in settings)
      let settings = {};
      try {
        settings = teamData.settings ? 
          (typeof teamData.settings === 'string' ? JSON.parse(teamData.settings) : teamData.settings) : {};
      } catch (e) {
        console.warn("Failed to parse settings:", e);
        settings = {};
      }

      // Find the sport_id from the sport name
      const sportName = teamData.sport_type || teamData.sport || "";
      // sport_type is stored lower-case (e.g. "basketball") while sport names
      // are title-case, so compare case-insensitively
      const sportKey = String(sportName).trim().toLowerCase();
      const matchingSport = sportsData.find(s => String(s.name || "").trim().toLowerCase() === sportKey);
      
      // Create the new form data object with all possible field mappings
      const newFormData = {
        name: teamData.name || "",
        description: teamData.description || "",
        sport: sportName,
        sport_id: matchingSport ? matchingSport.id : (teamData.sport_id || ""),
        organization: teamData.organization_name || teamData.organization || settings.organization || "",
        location: teamData.location || teamData.venue_name || settings.location || "",
        venue_address: teamData.venue_address || settings.venue_address || "",
        venue_lat: teamData.venue_lat || settings.venue_lat || null,
        venue_lng: teamData.venue_lng || settings.venue_lng || null,
        venue_place_id: teamData.venue_place_id || settings.venue_place_id || "",
        logo_url: teamData.logo_url || settings.logo_url || "",
        is_public: teamData.is_public !== undefined ? teamData.is_public : (settings.is_public !== undefined ? settings.is_public : true),
        skill_level: teamData.skill_level || settings.skill_level || "",
        requires_approval: teamData.requires_approval !== undefined ? teamData.requires_approval : (settings.requires_approval !== undefined ? settings.requires_approval : false),
        max_members: teamData.max_members || settings.max_members || 25,
        rules: {
          substitutions: teamData.rules?.substitutions || settings.rules?.substitutions || 1,
          age_restriction: teamData.rules?.age_restriction || settings.rules?.age_restriction || "none",
        },
      };

      // Set form data
      setFormData(newFormData);
    } catch (err) {
      console.error("Error fetching team:", err);
      setError("Failed to load team data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith("rules.")) {
      const ruleName = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        rules: {
          ...prev.rules,
          [ruleName]: value,
        },
      }));
    } else if (type === "radio") {
      // Handle radio buttons for boolean values - convert to proper boolean
      const boolValue = value === true || value === "true" || value === 'true';
      setFormData((prev) => ({
        ...prev,
        [name]: boolValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      setError("You must be logged in to edit teams.");
      return;
    }

      // Validate required fields
      if (!formData.name || !formData.name.trim()) {
        setError("Team name is required.");
        return;
      }

      if (!formData.sport_id && !formData.sport) {
        setError("Sport selection is required.");
        return;
      }
    
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      // Prepare update data matching the API expected format
      const updateData = {
        name: formData.name.trim(),
        description: formData.description,
        sport_id: formData.sport_id,
        sport_type: formData.sport,
        organization: formData.organization || null,
        logo_url: formData.logo_url,
        location: formData.location,
        // Venue + skill columns (previously never sent, so edits were lost)
        venue_name: formData.location || null,
        venue_address: formData.venue_address || null,
        venue_lat: formData.venue_lat ?? null,
        venue_lng: formData.venue_lng ?? null,
        venue_place_id: formData.venue_place_id || null,
        skill_level: formData.skill_level || null,
        is_public: formData.is_public,
        requires_approval: formData.requires_approval,
        max_members: parseInt(formData.max_members) || 25,
        rules: {
          substitutions: parseInt(formData.rules.substitutions) || 0,
          age_restriction: formData.rules.age_restriction,
        },
      };

      await teamService.updateTeam(teamId, updateData);

      setSuccess("Team updated successfully!");

      // Navigate to team details after save
      setTimeout(() => {
        navigate(`/teams/${teamId}`, { replace: true });
      }, 1200);
    } catch (err) {
      console.error("Error updating team:", err);
      setError("Failed to update team. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/teams/${teamId}`, { replace: true });
  };



  // Helper function to check if form has been modified
  const isFormModified = () => {
    if (!team) return false;
    // Add logic to compare current form data with original team data
    return JSON.stringify(formData) !== JSON.stringify({
      name: team.name || "",
      description: team.description || "",
      sport: team.sport_type || team.sport || "",
      // ... other fields
    });
  };

  if (loading) {
    return (
      <GameLayout
        token={token}
        onLogout={onLogout}
        profile={currentUser}
        showFloatingNav={true}
      >
        <div className="team-edit-gaming">
          <FloatingCard className="loading-card-gaming">
            <div className="loading-content-gaming">
              <div className="gaming-spinner"></div>
              <p className="loading-text">Loading team data...</p>
            </div>
          </FloatingCard>
        </div>
      </GameLayout>
    );
  }

  if (error && !team) {
    return (
      <GameLayout
        token={token}
        onLogout={onLogout}
        profile={currentUser}
        showFloatingNav={true}
      >
        <div className="team-edit-gaming">
          <FloatingCard className="error-card-gaming">
            <div className="error-content-gaming">
              <span className="error-icon">&#10007;</span>
              <h3 className="error-title">Error Loading Team</h3>
              <p className="error-message">{error}</p>
              <FloatingButton variant="primary" onClick={handleCancel} icon="Cancel">
                <span className="btn-text">Go Back</span>
              </FloatingButton>
            </div>
          </FloatingCard>
        </div>
      </GameLayout>
    );
  }

  return (
    <GameLayout
      token={token}
      onLogout={onLogout}
      profile={currentUser}
      showFloatingNav={true}
    >
      <div className="team-management-gaming">
        {team && (
          <>
            {/* Teams Navigation Header - Same as Team Details & Create Team */}
            <div className="teams-header-gaming">
              <FloatingCard className="header-card-gaming delay-1">
                <div className="teams-nav-gaming">
                  <div className="teams-nav-left">
                    <h2 className="teams-title-gaming">
                      Edit {team.name}
                    </h2>
                    <p className="teams-subtitle-gaming">
                      Update team information and settings
                    </p>
                  </div>
                </div>
              </FloatingCard>
            </div>

            {error && (
              <FloatingCard className="delay-2" style={{ marginBottom: '1rem', backgroundColor: 'rgba(220, 38, 38, 0.1)', border: '1px solid rgba(220, 38, 38, 0.3)' }}>
                <div className="error-alert-gaming">
                  <span className="alert-icon">&#9888;</span>
                  <span className="alert-text">{error}</span>
                </div>
              </FloatingCard>
            )}

            {success && (
              <FloatingCard className="delay-2" style={{ marginBottom: '1rem', backgroundColor: 'rgba(0, 255, 136, 0.1)', border: '1px solid rgba(0, 255, 136, 0.3)' }}>
                <div className="error-alert-gaming">
                  <span className="alert-icon">&#9888;</span>
                  <span className="alert-text">{success}</span>
                </div>
              </FloatingCard>
            )}

            <form onSubmit={handleSubmit} key={team.id}>
            {/* Team Information + Sport in one responsive 3-col row */}
            <div className="team-creator-cols" style={{ gap: '1rem', marginBottom: '1rem', alignItems: 'start' }}>

                {/* Col 1 â€” Basic Info */}
                <FloatingCard className="team-info-card-gaming delay-3">
                  <div className="card-header-gaming">
                    <h3 className="card-title-gaming"><span className="title-icon">📋</span> Team Information</h3>
                  </div>
                  <div className="card-content-gaming" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      <div>
                        <span className="info-label-gaming" style={{ color: 'rgba(255,255,255,0.9)', fontWeight: '500', marginBottom: '0.4rem', display: 'block' }}>Team Name *</span>
                        <Input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g., Thunder FC" required
                          style={{ width:'100%', padding:'0.5rem 0.75rem', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'6px', background:'rgba(255,255,255,0.05)', color:'white', fontSize:'0.9rem' }} />
                      </div>
                      <div>
                        <span className="info-label-gaming" style={{ color: 'rgba(255,255,255,0.9)', fontWeight: '500', marginBottom: '0.4rem', display: 'block' }}>Organization</span>
                        <Input type="text" name="organization" value={formData.organization} onChange={handleInputChange} placeholder="e.g., City Sports Club"
                          style={{ width:'100%', padding:'0.5rem 0.75rem', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'6px', background:'rgba(255,255,255,0.05)', color:'white', fontSize:'0.9rem' }} />
                      </div>
                      <div>
                        <span className="info-label-gaming" style={{ color: 'rgba(255,255,255,0.9)', fontWeight: '500', marginBottom: '0.4rem', display: 'block' }}>Team Description</span>
                        <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Tell people about your team..." rows={4}
                          style={{ width:'100%', padding:'0.75rem', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'8px', background:'rgba(255,255,255,0.05)', color:'white', fontSize:'0.9rem', resize:'vertical', fontFamily:'inherit' }} />
                      </div>
                  </div>
                </FloatingCard>

                {/* Col 2 - Sport + Location */}
                <FloatingCard className="team-info-card-gaming delay-4">
                  <div className="card-header-gaming">
                    <h3 className="card-title-gaming"><span className="title-icon">&#9917;</span> Sport &amp; Location</h3>
                  </div>
                  <div className="card-content-gaming" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <span className="info-label-gaming" style={{ color: 'rgba(255,255,255,0.9)', fontWeight: '500', marginBottom: '0.4rem', display: 'block' }}>Sport</span>
                      <div style={{ padding:'0.5rem 0.75rem', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', background:'rgba(255,255,255,0.03)', color:'rgba(255,255,255,0.6)', fontSize:'0.9rem' }}>
                        {formData.sport || 'Unknown'}
                      </div>
                    </div>
                    <div>
                      <span className="info-label-gaming" style={{ color: 'rgba(255,255,255,0.9)', fontWeight: '500', marginBottom: '0.4rem', display: 'block' }}>Location</span>
                      <VenueSearch
                        label=""
                        value={{
                          venue: formData.location,
                          venue_place_id: formData.venue_place_id,
                          venue_address: formData.venue_address,
                          venue_lat: formData.venue_lat,
                          venue_lng: formData.venue_lng,
                          court: '',
                        }}
                        onChange={(v) => setFormData(prev => ({ ...prev, location: v.venue, venue_address: v.venue_address, venue_lat: v.venue_lat, venue_lng: v.venue_lng, venue_place_id: v.venue_place_id }))}
                      />
                    </div>
                  </div>
                </FloatingCard>

                {/* Col 3 - Team Settings */}
                <FloatingCard className="team-info-card-gaming delay-5">
                  <div className="card-header-gaming">
                    <h3 className="card-title-gaming"><span className="title-icon">&#9881;</span> Team Settings</h3>
                  </div>
                  <div className="card-content-gaming" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <span style={{ color:'rgba(255,255,255,0.9)', fontWeight:'500', marginBottom:'0.4rem', display:'block', fontSize:'0.85rem' }}>Privacy</span>
                      <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
                        <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer', fontSize:'0.88rem', color:'rgba(255,255,255,0.9)' }}>
                          <input type="radio" checked={formData.is_public === true} onChange={() => setFormData(p => ({ ...p, is_public: true }))} /> Public
                        </label>
                        <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer', fontSize:'0.88rem', color:'rgba(255,255,255,0.9)' }}>
                          <input type="radio" checked={formData.is_public === false} onChange={() => setFormData(p => ({ ...p, is_public: false }))} /> Private
                        </label>
                      </div>
                    </div>
                    <div>
                      <span style={{ color:'rgba(255,255,255,0.9)', fontWeight:'500', marginBottom:'0.4rem', display:'block', fontSize:'0.85rem' }}>Join Approval</span>
                      <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
                        <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer', fontSize:'0.88rem', color:'rgba(255,255,255,0.9)' }}>
                          <input type="radio" checked={formData.requires_approval === true} onChange={() => setFormData(p => ({ ...p, requires_approval: true }))} /> Manual Approval
                        </label>
                        <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer', fontSize:'0.88rem', color:'rgba(255,255,255,0.9)' }}>
                          <input type="radio" checked={formData.requires_approval === false} onChange={() => setFormData(p => ({ ...p, requires_approval: false }))} /> Auto Join
                        </label>
                      </div>
                    </div>
                    <div>
                      <span style={{ color:'rgba(255,255,255,0.9)', fontWeight:'500', marginBottom:'0.4rem', display:'block', fontSize:'0.85rem' }}>Max Members</span>
                      <input type="text" inputMode="numeric" pattern="[0-9]*" name="max_members"
                        value={formData.max_members === '' ? '' : formData.max_members}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, '');
                          if (raw === '') {
                            setFormData(p => ({ ...p, max_members: '' }));
                          } else {
                            const v = parseInt(raw, 10);
                            setFormData(p => ({ ...p, max_members: Math.min(999, v) }));
                          }
                        }}
                        onBlur={(e) => {
                          const v = parseInt(e.target.value, 10);
                          setFormData(p => ({ ...p, max_members: Number.isNaN(v) || v < 2 ? 2 : Math.min(v, 999) }));
                        }}
                        placeholder="2 – 999"
                        style={{ width:'100%', padding:'0.5rem 0.75rem', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'6px', background:'rgba(255,255,255,0.05)', color:'white', fontSize:'0.9rem' }} />
                    </div>
                    <div>
                      <span style={{ color:'rgba(255,255,255,0.9)', fontWeight:'500', marginBottom:'0.4rem', display:'block', fontSize:'0.85rem' }}>Substitutions</span>
                      <input type="text" inputMode="numeric" pattern="[0-9]*" name="rules.substitutions"
                        value={formData.rules.substitutions === '' ? '' : formData.rules.substitutions}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, '');
                          if (raw === '') {
                            setFormData(p => ({ ...p, rules: { ...p.rules, substitutions: '' } }));
                          } else {
                            const v = parseInt(raw, 10);
                            setFormData(p => ({ ...p, rules: { ...p.rules, substitutions: Math.min(99, v) } }));
                          }
                        }}
                        onBlur={(e) => {
                          const v = parseInt(e.target.value, 10);
                          setFormData(p => ({ ...p, rules: { ...p.rules, substitutions: Number.isNaN(v) || v < 0 ? 0 : Math.min(v, 99) } }));
                        }}
                        placeholder="0 – 99"
                        style={{ width:'100%', padding:'0.5rem 0.75rem', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'6px', background:'rgba(255,255,255,0.05)', color:'white', fontSize:'0.9rem' }} />
                    </div>
                    <div>
                      <span style={{ color:'rgba(255,255,255,0.9)', fontWeight:'500', marginBottom:'0.4rem', display:'block', fontSize:'0.85rem' }}>Age Restriction</span>
                      <select name="rules.age_restriction" value={formData.rules.age_restriction} onChange={handleInputChange}
                        style={{ width:'100%', padding:'0.5rem 0.75rem', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'6px', background:'rgba(30,30,50,0.95)', color:'white', fontSize:'0.9rem', cursor:'pointer' }}>
                        <option value="none" style={{ background:'#1a1a2e' }}>No Restriction</option>
                        <option value="18+" style={{ background:'#1a1a2e' }}>18+ Only</option>
                        <option value="21+" style={{ background:'#1a1a2e' }}>21+ Only</option>
                        <option value="under18" style={{ background:'#1a1a2e' }}>Under 18</option>
                      </select>
                    </div>
                    <div>
                      <span style={{ color:'rgba(255,255,255,0.9)', fontWeight:'500', marginBottom:'0.4rem', display:'block', fontSize:'0.85rem' }}>🏅 Skill Level</span>
                      <select name="skill_level" value={formData.skill_level || ""} onChange={e => setFormData(p => ({ ...p, skill_level: e.target.value || null }))}
                        style={{ width:'100%', padding:'0.5rem 0.75rem', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'6px', background:'rgba(30,30,50,0.95)', color:'white', fontSize:'0.9rem', cursor:'pointer' }}>
                        <option value="" style={{ background:'#1a1a2e' }}>Any skill level</option>
                        <option value="beginner" style={{ background:'#1a1a2e' }}>🌿 Beginner</option>
                        <option value="recreational" style={{ background:'#1a1a2e' }}>🌱 Recreational</option>
                        <option value="intermediate" style={{ background:'#1a1a2e' }}>⚡ Intermediate</option>
                        <option value="advanced" style={{ background:'#1a1a2e' }}>🔥 Advanced</option>
                        <option value="professional" style={{ background:'#1a1a2e' }}>👑 Professional</option>
                      </select>
                    </div>
                  </div>
                </FloatingCard>

            </div>

            {/* Action Buttons */}
            <FloatingCard className="delay-6" style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <FloatingButton type="button" variant="outline" size="medium" onClick={handleCancel} disabled={saving}>
                  <span className="btn-text">Cancel</span>
                </FloatingButton>
                <FloatingButton type="submit" variant="primary" size="medium" disabled={saving}>
                  <span className="btn-text">{saving ? 'Updating...' : 'Update Team'}</span>
                </FloatingButton>
              </div>
            </FloatingCard>
          </form>
          </>
        )}
      </div>
    </GameLayout>
  );
};

export default EditTeam;
